import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAnalyticsScope } from '@/hooks'
import { useScopePortals } from '@/application/queries'
import { Base } from '@itsrever/design-system'
import { renderChecklistCount, useChecklistLabels } from '@/ui/shared/checklist'

export const PortalSelector = () => {
  const { t } = useTranslation()
  const labels = useChecklistLabels(t('portal_selector.search'))
  const { portals, loadingPortals } = useScopePortals()
  const { selectedIds, isEveryPortal, isGroupView, setScope } =
    useAnalyticsScope()

  // Alphabetical, the only order a caller can predict. Siblings share one store name,
  // so only the ecommerce id tells them apart.
  const options = useMemo(
    () =>
      portals
        .map((p) => ({ value: p.id, label: p.id }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [portals]
  )
  const everyIds = useMemo(() => options.map((o) => o.value), [options])

  // The scope is stored empty to mean "every portal", so portals added later join on their own.
  const apply = (draft: string[]) => {
    const drafted = new Set(draft)
    const everySelected =
      draft.length === everyIds.length &&
      everyIds.every((id) => drafted.has(id))
    setScope(everySelected ? [] : draft)
  }

  const label = isEveryPortal
    ? t('portal_selector.all')
    : selectedIds.length === 1
      ? selectedIds[0]
      : t('portal_selector.count', { count: selectedIds.length })

  // Only the group view has a scope to narrow: with one portal picked in the navbar there is
  // nothing to choose, and two controls fighting over the same thing is worse than one.
  if (!isGroupView || loadingPortals || portals.length < 2) return null

  return (
    <Base.ChecklistPopover
      trigger={
        <>
          <span className="max-w-[200px] truncate">{label}</span>
          <ChevronDown />
        </>
      }
      count={isEveryPortal ? 0 : selectedIds.length}
      renderCount={renderChecklistCount}
      title={t('portal_selector.select')}
      options={options}
      selected={isEveryPortal ? everyIds : selectedIds}
      onApply={apply}
      requireSelection
      labels={labels}
    />
  )
}

const ChevronDown = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    className="text-neutral-400"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
)
